import { useState, useEffect, useCallback } from 'react'
import { api } from '../context/AuthContext'
import { API_FILE_URL } from '../constant/api'

// ── Status mapping ────────────────────────────────────────────────────────────
const STATUS_MAP = {
  0: { bg: '#fef2f2', color: '#b91c1c', dot: '#ef4444', label: 'Suspended' },
  1: { bg: '#f0fdf4', color: '#15803d', dot: '#16a34a', label: 'Active'    },
}
const statusColor = (s) => STATUS_MAP[s] ?? { bg: '#f3f4f6', color: '#374151', dot: '#9ca3af', label: '—' }

const FILTER_OPTIONS = [
  { label: 'All',       value: ''  },
  { label: 'Active',    value: '1' },
  { label: 'Suspended', value: '0' },
]

const PAYOUT_LABELS = { mtn: 'MTN MoMo', airtel: 'Airtel Money', bank: 'Bank Account' }

const VERIFICATION_MAP = {
  unverified: { bg: '#f3f4f6', color: '#374151', dot: '#9ca3af', label: 'Unverified' },
  pending:    { bg: '#fffbeb', color: '#b45309', dot: '#f59e0b', label: 'Pending'    },
  verified:   { bg: '#f0fdf4', color: '#15803d', dot: '#16a34a', label: 'Verified'   },
  rejected:   { bg: '#fef2f2', color: '#b91c1c', dot: '#ef4444', label: 'Rejected'   },
}
const verificationColor = (s) => VERIFICATION_MAP[s] ?? VERIFICATION_MAP.unverified

const TIER_INFO = {
  bronze:   { emoji: '🥉', label: 'Bronze',   color: '#92400e' },
  silver:   { emoji: '🥈', label: 'Silver',   color: '#475569' },
  gold:     { emoji: '🥇', label: 'Gold',     color: '#713f12' },
  platinum: { emoji: '💎', label: 'Platinum', color: '#3730a3' },
}

const avatarBg = (str = '') => {
  const colors = ['#fde8d8','#dff5e8','#e8f4ff','#fef3d0','#ede8ff','#fce7f3']
  let h = 0
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
  return colors[Math.abs(h) % colors.length]
}
const initials = (name = '') => name.trim().charAt(0).toUpperCase() || '?'
const fmt      = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'
const fmtTime  = (d) => d ? new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—'
const money    = (n) => n != null ? `${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RWF` : '—'

const API_BASE = process.env.REACT_APP_API_URL || API_FILE_URL
const creatorAvatarUrl = (avatar) => {
  if (!avatar) return null
  if (avatar.startsWith('http')) return avatar
  return `${API_BASE}/${avatar}`
}

const selectStyle = { border: '1px solid #e9e9e4', borderRadius: 7, padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#111317', background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" }

// ── Sub-components ────────────────────────────────────────────────────────────
function StatCard({ label, value, accent }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '18px 20px', flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.6px', color: accent || '#111317' }}>{value}</div>
    </div>
  )
}

function StatusBadge({ status }) {
  const s = statusColor(status)
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: s.bg, color: s.color, fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '3px 8px', borderRadius: 4, textTransform: 'uppercase' }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot }} />{s.label}
    </span>
  )
}

function VerificationBadge({ status }) {
  const s = verificationColor(status)
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: s.bg, color: s.color, fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '3px 8px', borderRadius: 4, textTransform: 'uppercase' }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot }} />KYC: {s.label}
    </span>
  )
}

function CreatorAvatar({ name = '', avatar, size = 40, radius = 9, fontSize = 16 }) {
  const url = creatorAvatarUrl(avatar)
  const [imgErr, setImgErr] = useState(false)
  if (url && !imgErr) {
    return <img src={url} alt={name} onError={() => setImgErr(true)} style={{ width: size, height: size, borderRadius: radius, objectFit: 'cover', flexShrink: 0 }} />
  }
  return (
    <div style={{ width: size, height: size, borderRadius: radius, background: avatarBg(name), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize, fontWeight: 800, color: '#111317', flexShrink: 0 }}>
      {initials(name)}
    </div>
  )
}

// ── Edit Creator Modal ────────────────────────────────────────────────────────
function EditCreatorModal({ creator, categories, onClose, onSaved }) {
  const [form, setForm] = useState({
    display_name:   creator.display_name   || '',
    category:       creator.category       || '',
    bio:            creator.bio            || '',
    payout_method:  creator.payout_method  || '',
    payout_account: creator.payout_account || '',
    bank_name:      creator.bank_name      || '',
  })
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState('')
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const INP = { width:'100%', height:38, borderRadius:8, border:'1px solid #e9e9e4', padding:'0 12px', fontSize:13, outline:'none', fontFamily:"'Plus Jakarta Sans',sans-serif", boxSizing:'border-box', background:'#fff' }
  const LBL = { display:'block', fontSize:11, fontWeight:700, color:'#111317', textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'JetBrains Mono',monospace", marginBottom:6 }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.display_name.trim()) { setError('Display name is required'); return }
    setSaving(true); setError('')
    try {
      const res = await api.patch(`/creators/admin/${creator.creator_id}`, {
        displayName:   form.display_name,
        category:      form.category,
        bio:           form.bio,
        ...(form.payout_method ? { payoutMethod: form.payout_method, payoutAccount: form.payout_account, bankName: form.bank_name } : {}),
      })
      onSaved(res.data.creator)
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save changes')
    } finally { setSaving(false) }
  }

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', zIndex:500 }} />
      <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:480, maxHeight:'92vh', background:'#fff', borderRadius:16, zIndex:501, display:'flex', flexDirection:'column', boxShadow:'0 24px 80px rgba(0,0,0,0.2)', overflow:'hidden' }}>
        <div style={{ padding:'18px 24px', borderBottom:'1px solid #e9e9e4', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <div style={{ fontSize:16, fontWeight:800 }}>✏️ Edit Creator</div>
            <div style={{ fontSize:12, color:'#9a9a94', marginTop:2, fontFamily:"'JetBrains Mono',monospace" }}>#{creator.creator_id} — {creator.display_name}</div>
          </div>
          <div onClick={onClose} style={{ cursor:'pointer', fontSize:20, color:'#9a9a94', lineHeight:1 }}>×</div>
        </div>

        <div style={{ flex:1, overflowY:'auto', padding:'20px 24px' }}>
          <form id="editCreatorForm" onSubmit={handleSave} style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div>
              <label style={LBL}>Display Name *</label>
              <input required value={form.display_name} onChange={e => set('display_name', e.target.value)} style={INP} />
            </div>
            <div>
              <label style={LBL}>Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)} style={{ ...INP, cursor:'pointer' }}>
                <option value="">— None —</option>
                {/* Keep the creator's current value selectable even if it predates the managed list */}
                {form.category && !categories.some(c => c.name.toLowerCase() === form.category.toLowerCase()) && (
                  <option value={form.category}>{form.category} (legacy)</option>
                )}
                {categories.map(c => <option key={c.category_id} value={c.name}>{c.icon ? `${c.icon} ` : ''}{c.name}</option>)}
              </select>
              <div style={{ fontSize:11, color:'#9a9a94', marginTop:4 }}>Manage the list in Settings → Creator Categories</div>
            </div>
            <div>
              <label style={LBL}>Bio</label>
              <textarea value={form.bio} onChange={e => set('bio', e.target.value)} placeholder="Short creator bio…" style={{ ...INP, height:72, padding:'8px 12px', resize:'vertical' }} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={LBL}>Payout Method</label>
                <select value={form.payout_method} onChange={e => set('payout_method', e.target.value)} style={{ ...INP, cursor:'pointer' }}>
                  <option value="">Unchanged</option>
                  <option value="mtn">MTN MoMo</option>
                  <option value="airtel">Airtel Money</option>
                  <option value="bank">Bank Account</option>
                </select>
              </div>
              <div>
                <label style={LBL}>Payout Account</label>
                <input value={form.payout_account} onChange={e => set('payout_account', e.target.value)} placeholder="Phone or account no." style={INP} />
              </div>
            </div>
            {error && <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:7, padding:'9px 13px', fontSize:12, color:'#b91c1c', fontWeight:600 }}>⚠ {error}</div>}
          </form>
        </div>

        <div style={{ padding:'14px 24px', borderTop:'1px solid #e9e9e4', display:'flex', gap:10, flexShrink:0 }}>
          <button type="button" onClick={onClose} style={{ flex:1, height:42, borderRadius:8, border:'1px solid #e9e9e4', background:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
          <button type="submit" form="editCreatorForm" disabled={saving} style={{ flex:2, height:42, borderRadius:8, border:'none', background:saving?'#f0c4b4':'#e8440a', color:'#fff', fontSize:13, fontWeight:700, cursor:saving?'not-allowed':'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </>
  )
}

// ── KYC Tab ────────────────────────────────────────────────────────────────────
const CREATOR_DOC_LABELS = {
  national_id:    'National ID / Passport',
  selfie_with_id: 'Selfie Holding ID',
  other:          'Other Document',
}

function KycTab({ creatorId, onStatusChange }) {
  const [docs,    setDocs]    = useState([])
  const [loading, setLoading] = useState(true)
  const [saving,  setSaving]  = useState(null)
  const [note,    setNote]    = useState({})

  const reportStatus = useCallback((documents) => {
    if (!onStatusChange) return
    if (!documents.length) { onStatusChange('unverified'); return }
    const required    = documents.filter(d => d.doc_type === 'national_id' || d.doc_type === 'selfie_with_id')
    const hasRejected = required.some(d => d.status === 'rejected')
    const hasId       = required.some(d => d.doc_type === 'national_id'    && d.status === 'approved')
    const hasSelfie    = required.some(d => d.doc_type === 'selfie_with_id' && d.status === 'approved')
    onStatusChange(hasRejected ? 'rejected' : (hasId && hasSelfie) ? 'verified' : 'pending')
  }, [onStatusChange])

  useEffect(() => {
    setLoading(true)
    api.get(`/creators/admin/${creatorId}/documents`)
      .then(res => { const d = res.data.documents || []; setDocs(d); reportStatus(d) })
      .catch(() => { setDocs([]); onStatusChange?.('unverified') })
      .finally(() => setLoading(false))
  }, [creatorId]) // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (doc, status) => {
    setSaving(doc.doc_id)
    try {
      const res = await api.patch(`/creators/admin/documents/${doc.doc_id}/status`, { status, admin_note: note[doc.doc_id] || '' })
      const next = docs.map(d => d.doc_id === doc.doc_id ? res.data.document : d)
      setDocs(next)
      reportStatus(next)
    } catch { /* keep optimistic */ }
    setSaving(null)
  }

  const STATUS = {
    pending:  { color: '#b45309', bg: '#fffbeb', border: '#fde68a', dot: '#f59e0b', label: 'Pending' },
    approved: { color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', dot: '#16a34a', label: 'Approved' },
    rejected: { color: '#b91c1c', bg: '#fef2f2', border: '#fecaca', dot: '#ef4444', label: 'Rejected' },
  }

  if (loading) return <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>Loading documents…</div>

  if (docs.length === 0) return (
    <div style={{ textAlign: 'center', padding: '40px 0' }}>
      <div style={{ fontSize: 32, marginBottom: 10 }}>📭</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#111317', marginBottom: 4 }}>No documents uploaded</div>
      <div style={{ fontSize: 12, color: '#9a9a94' }}>This creator hasn't uploaded any verification documents yet.</div>
    </div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {(() => {
        const approved = docs.filter(d => d.status === 'approved').length
        const pending  = docs.filter(d => d.status === 'pending').length
        const rejected = docs.filter(d => d.status === 'rejected').length
        return (
          <div style={{ background: approved === docs.length ? '#f0fdf4' : pending > 0 ? '#fffbeb' : '#fef2f2', border: `1px solid ${approved === docs.length ? '#bbf7d0' : pending > 0 ? '#fde68a' : '#fecaca'}`, borderRadius: 9, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>{approved === docs.length ? '✅' : pending > 0 ? '⏳' : '❌'}</span>
            <div style={{ fontSize: 12, fontWeight: 700 }}>
              {approved} approved · {pending} pending · {rejected} rejected — {docs.length} total
            </div>
          </div>
        )
      })()}

      {docs.map(doc => {
        const s = STATUS[doc.status] || STATUS.pending
        return (
          <div key={doc.doc_id} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
              <span style={{ fontSize: 24, flexShrink: 0 }}>{doc.file_url?.toLowerCase().endsWith('.pdf') ? '📋' : '🖼'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 3 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#111317', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>{doc.file_name}</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 9, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', color: s.color, background: 'rgba(255,255,255,0.7)', border: `1px solid ${s.border}`, borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap' }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot }} />
                    {s.label}
                  </span>
                </div>
                <div style={{ fontSize: 10, color: '#6b7280', fontFamily: "'JetBrains Mono',monospace" }}>
                  {CREATOR_DOC_LABELS[doc.doc_type] || doc.doc_type} · {new Date(doc.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
              </div>
              <a href={doc.file_url} target="_blank" rel="noreferrer"
                style={{ height: 30, padding: '0 12px', borderRadius: 6, border: `1px solid ${s.border}`, background: 'rgba(255,255,255,0.7)', color: '#374151', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>
                View ↗
              </a>
            </div>

            {doc.status !== 'approved' && (
              <input
                value={note[doc.doc_id] || doc.admin_note || ''}
                onChange={e => setNote(n => ({ ...n, [doc.doc_id]: e.target.value }))}
                placeholder="Admin note (optional, shown to creator)"
                style={{ width: '100%', height: 32, border: `1px solid ${s.border}`, borderRadius: 6, padding: '0 10px', fontSize: 12, outline: 'none', marginBottom: 8, boxSizing: 'border-box', background: 'rgba(255,255,255,0.6)', fontFamily: "'Plus Jakarta Sans',sans-serif" }}
              />
            )}
            {doc.status === 'approved' && doc.admin_note && (
              <div style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic', marginBottom: 8 }}>Note: {doc.admin_note}</div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              {doc.status !== 'approved' && (
                <button onClick={() => setStatus(doc, 'approved')} disabled={saving === doc.doc_id}
                  style={{ flex: 1, height: 30, borderRadius: 6, border: '1px solid #bbf7d0', background: saving === doc.doc_id ? '#d1fae5' : '#f0fdf4', color: '#15803d', fontSize: 11, fontWeight: 700, cursor: saving === doc.doc_id ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  ✓ Approve
                </button>
              )}
              {doc.status !== 'rejected' && (
                <button onClick={() => setStatus(doc, 'rejected')} disabled={saving === doc.doc_id}
                  style={{ flex: 1, height: 30, borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: saving === doc.doc_id ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  ✕ Reject
                </button>
              )}
              {doc.status === 'approved' && (
                <button onClick={() => setStatus(doc, 'pending')} disabled={saving === doc.doc_id}
                  style={{ height: 30, padding: '0 12px', borderRadius: 6, border: '1px solid #e9e9e4', background: '#fff', color: '#9a9a94', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  Revoke
                </button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Payout Tab ─────────────────────────────────────────────────────────────────
function PayoutTab({ creator, onResolved }) {
  const [resolving, setResolving] = useState(null)

  const resolve = async (req, status) => {
    setResolving(req.request_id)
    try {
      const res = await api.patch(`/creators/admin/payout-requests/${req.request_id}`, { status })
      onResolved(res.data.request)
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update payout request')
    } finally { setResolving(null) }
  }

  const REQ_STATUS = {
    pending:  { color: '#b45309', bg: '#fffbeb', border: '#fde68a', label: 'Pending'  },
    paid:     { color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', label: 'Paid'     },
    rejected: { color: '#b91c1c', bg: '#fef2f2', border: '#fecaca', label: 'Rejected' },
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ background: '#fff3f0', border: '1px solid #fdd0c0', borderRadius: 10, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#e8440a', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace" }}>Payable Balance</div>
          <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 3 }}>Net of platform commission, not yet requested</div>
        </div>
        <div style={{ fontSize: 18, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#e8440a' }}>{money(creator.payable_balance)}</div>
      </div>

      {creator.verification_status !== 'verified' && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 9, padding: '10px 14px', fontSize: 12, color: '#92400e' }}>
          ⚠ KYC not yet verified — this creator can't request a payout until their identity documents are approved.
        </div>
      )}

      {!creator.payout_requests?.length ? (
        <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '32px 0' }}>No payout requests yet.</div>
      ) : creator.payout_requests.map(req => {
        const s = REQ_STATUS[req.status] || REQ_STATUS.pending
        return (
          <div key={req.request_id} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: s.color }}>{money(req.amount)}</span>
              <span style={{ fontSize: 9, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', color: s.color, background: 'rgba(255,255,255,0.7)', border: `1px solid ${s.border}`, borderRadius: 4, padding: '1px 6px' }}>{s.label}</span>
            </div>
            <div style={{ fontSize: 11, color: '#6b7280', fontFamily: "'JetBrains Mono',monospace", marginBottom: req.status === 'pending' ? 10 : 0 }}>
              {PAYOUT_LABELS[req.payout_method] || req.payout_method || '—'} · {req.payout_account || '—'} · requested {fmtTime(req.requested_at)}
            </div>
            {req.status === 'pending' && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => resolve(req, 'paid')} disabled={resolving === req.request_id}
                  style={{ flex: 1, height: 30, borderRadius: 6, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#15803d', fontSize: 11, fontWeight: 700, cursor: resolving === req.request_id ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  ✓ Mark Paid
                </button>
                <button onClick={() => resolve(req, 'rejected')} disabled={resolving === req.request_id}
                  style={{ flex: 1, height: 30, borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: resolving === req.request_id ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  ✕ Reject
                </button>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Creator Drawer ────────────────────────────────────────────────────────────
function CreatorDrawer({ creator, categories, onClose, onStatusChange }) {
  const [tab,        setTab]        = useState('details')
  const [detail,     setDetail]     = useState(null)
  const [detailLoad, setDetailLoad] = useState(false)
  const [editing,    setEditing]    = useState(false)
  const [overriding,     setOverriding]     = useState(false)
  const [overrideTier,   setOverrideTier]   = useState('')
  const [overrideReason, setOverrideReason] = useState('')
  const [overrideSaving, setOverrideSaving] = useState(false)

  useEffect(() => {
    if (!creator) return
    setTab('details')
    setDetail(null)
    setDetailLoad(true)
    api.get(`/creators/admin/${creator.creator_id}`)
      .then(res => setDetail(res.data.creator))
      .catch(() => setDetail(creator))
      .finally(() => setDetailLoad(false))
  }, [creator?.creator_id]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!creator) return null
  const d  = detail || creator
  const bg = avatarBg(d.display_name || '')

  const saveOverride = async () => {
    setOverrideSaving(true)
    try {
      const res = await api.put(`/tiers/admin/creators/${d.creator_id}/override`, {
        tier: overrideTier || null,
        reason: overrideReason,
      })
      setDetail(prev => ({
        ...prev,
        tier: {
          ...prev.tier,
          effective_tier:   res.data.override?.tier || prev.tier.computed_tier,
          is_overridden:    !!res.data.override,
          override_reason:  res.data.override?.reason || null,
        },
      }))
      setOverriding(false)
      setOverrideReason('')
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save tier override')
    } finally { setOverrideSaving(false) }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)', zIndex: 200 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 520, background: '#fff', zIndex: 201, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 40px rgba(0,0,0,0.12)', animation: 'slideIn 0.22s ease' }}>
        <style>{`@keyframes slideIn { from { transform:translateX(40px);opacity:0 } to { transform:translateX(0);opacity:1 } }`}</style>

        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 46, height: 46, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800, color: '#111317', flexShrink: 0, overflow: 'hidden' }}>
            {creatorAvatarUrl(d.avatar)
              ? <img src={creatorAvatarUrl(d.avatar)} alt={d.display_name} style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e => e.target.style.display='none'} />
              : initials(d.display_name)
            }
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: 15, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.display_name}</div>
              {d.is_live && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fef2f2', color: '#ef4444', fontSize: 9, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", padding: '2px 7px', borderRadius: 4, textTransform: 'uppercase', flexShrink: 0 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />LIVE
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>#{d.creator_id} · {d.owner_name || d.owner_phone || '—'}</div>
          </div>
          <StatusBadge status={d.status} />
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94', marginLeft: 8, lineHeight: 1 }}>×</div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e9e9e4', padding: '0 24px' }}>
          {[['details','Details'],['kyc','KYC'],['tips','Tips'],['videos','Videos'],['payout','Payout'],['live','Live History']].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{ padding: '12px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif", color: tab === id ? '#111317' : '#9a9a94', borderBottom: `2px solid ${tab === id ? '#e8440a' : 'transparent'}`, marginBottom: -1, whiteSpace: 'nowrap' }}>
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {detailLoad && <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '32px 0' }}>Loading…</div>}

          {/* ── Details ── */}
          {!detailLoad && tab === 'details' && (
            <>
              {[
                ['Creator ID',     d.creator_id],
                ['Display Name',   d.display_name],
                ['Owner',          d.owner_name    || '—'],
                ['Owner Phone',    d.owner_phone   || '—'],
                ['Owner Email',    d.owner_email   || '—'],
                ['Category',       d.category      || '—'],
                ['Bio',            d.bio           || '—'],
                ['Status',         <StatusBadge status={d.status} />],
                ['Verification',   <VerificationBadge status={d.verification_status} />],
                ['Followers',      d.follower_count ?? 0],
                ['Total Tips',     money(d.total_tips)],
                ['Video Earnings', money(d.video_earnings?.total_amount)],
                ['Kudos Premium Earnings',  money(d.pass_earnings?.total_net)],
                ['Payout Schedule', d.payout_schedule === 'on_demand' ? '⚡ On Demand' : '📅 Monthly'],
                ['Joined',         fmt(d.created_at)],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f3f3f0' }}>
                  <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600, flexShrink: 0, marginRight: 12 }}>{k}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#111317', textAlign: 'right', maxWidth: 280, wordBreak: 'break-word' }}>{v}</span>
                </div>
              ))}

              {/* Tier block */}
              {d.tier && (
                <div style={{ marginTop: 14, background: '#f9f9f7', border: '1px solid #e9e9e4', borderRadius: 10, padding: '14px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace" }}>Creator Tier</div>
                    <span onClick={() => setOverriding(o => !o)} style={{ fontSize: 11, color: '#e8440a', fontWeight: 700, cursor: 'pointer' }}>{overriding ? 'Cancel' : 'Override'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 20 }}>{TIER_INFO[d.tier.effective_tier]?.emoji}</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: TIER_INFO[d.tier.effective_tier]?.color }}>
                        {TIER_INFO[d.tier.effective_tier]?.label}
                        {d.tier.is_overridden && <span style={{ fontSize: 10, color: '#7c3aed', marginLeft: 6 }}>(override)</span>}
                      </div>
                      <div style={{ fontSize: 11, color: '#9a9a94' }}>{Number(d.tier.views).toLocaleString()} cumulative views · {(d.tier.discount * 100).toFixed(0)}pp commission discount</div>
                    </div>
                  </div>
                  {d.tier.next_tier && (
                    <div style={{ fontSize: 11, color: '#9a9a94' }}>
                      {Number(d.tier.next_tier.views_remaining).toLocaleString()} views to {TIER_INFO[d.tier.next_tier.name]?.label} ({d.tier.next_tier.views_pct}%)
                    </div>
                  )}
                  {overriding && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e9e9e4', display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <select value={overrideTier} onChange={e => setOverrideTier(e.target.value)} style={{ ...selectStyle, width: '100%' }}>
                        <option value="">Clear override</option>
                        <option value="bronze">Bronze</option>
                        <option value="silver">Silver</option>
                        <option value="gold">Gold</option>
                        <option value="platinum">Platinum</option>
                      </select>
                      <input value={overrideReason} onChange={e => setOverrideReason(e.target.value)} placeholder="Reason (optional)"
                        style={{ height: 32, border: '1px solid #e9e9e4', borderRadius: 6, padding: '0 10px', fontSize: 12, outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
                      <button onClick={saveOverride} disabled={overrideSaving}
                        style={{ height: 32, borderRadius: 6, border: 'none', background: overrideSaving ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 11, fontWeight: 700, cursor: overrideSaving ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                        {overrideSaving ? 'Saving…' : 'Save Override'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Payout block */}
              <div style={{ marginTop: 14, background: d.payout_account ? '#f0fdf4' : '#fffbeb', border: `1px solid ${d.payout_account ? '#bbf7d0' : '#fde68a'}`, borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: d.payout_account ? '#15803d' : '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 10 }}>
                  💸 Payout {d.payout_account ? '— on file' : '— NOT SET'}
                </div>
                {d.payout_account ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Method</span>
                      <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#111317' }}>{PAYOUT_LABELS[d.payout_method] || d.payout_method}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Account</span>
                      <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#111317' }}>{d.payout_account}</span>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: '#92400e' }}>
                    No payout method on file yet — tips can't be sent until one is set.
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── KYC ── */}
          {!detailLoad && tab === 'kyc' && (
            <KycTab creatorId={d.creator_id} onStatusChange={vs => setDetail(prev => ({ ...prev, verification_status: vs }))} />
          )}

          {/* ── Payout ── */}
          {!detailLoad && tab === 'payout' && (
            <PayoutTab
              creator={d}
              onResolved={(updatedReq) => setDetail(prev => ({
                ...prev,
                payout_requests: (prev.payout_requests || []).map(r => r.request_id === updatedReq.request_id ? updatedReq : r),
              }))}
            />
          )}

          {/* ── Tips ── */}
          {!detailLoad && tab === 'tips' && (
            !d.tips?.length
              ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>No tips received yet.</div>
              : <div>
                  <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginBottom: 12 }}>{d.tips.length} tip{d.tips.length !== 1 ? 's' : ''}</div>
                  {d.tips.map((t, i) => (
                    <div key={t.tip_id || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #f3f3f0' }}>
                      <span style={{ fontSize: 18 }}>{t.emoji || '💰'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 700 }}>{t.buyer_name || 'Anonymous'}</div>
                        <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmtTime(t.created_at)}</div>
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>{money(t.amount)}</span>
                    </div>
                  ))}
                </div>
          )}

          {/* ── Videos ── */}
          {!detailLoad && tab === 'videos' && (
            <>
              {d.video_earnings && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 8 }}>
                  {[
                    ['Unlocks',      d.video_earnings.total_count, '#374151'],
                    ['Gross', money(d.video_earnings.total_amount), '#374151'],
                    ['Net',   money(d.video_earnings.total_net),    '#15803d'],
                  ].map(([lbl, val, color]) => (
                    <div key={lbl} style={{ background: '#f9f9f7', borderRadius: 8, padding: '10px 12px', border: '1px solid #e9e9e4' }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 4 }}>{lbl}</div>
                      <div style={{ fontSize: 13, fontWeight: 800, color, fontFamily: "'JetBrains Mono',monospace" }}>{val}</div>
                    </div>
                  ))}
                </div>
              )}
              {d.pass_earnings && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 16 }}>
                  {[
                    ['Kudos Premium Payouts', d.pass_earnings.total_count, '#374151'],
                    ['Kudos Premium Earnings (Net)', money(d.pass_earnings.total_net), '#15803d'],
                  ].map(([lbl, val, color]) => (
                    <div key={lbl} style={{ background: '#f9f9f7', borderRadius: 8, padding: '10px 12px', border: '1px solid #e9e9e4' }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 4 }}>{lbl}</div>
                      <div style={{ fontSize: 13, fontWeight: 800, color, fontFamily: "'JetBrains Mono',monospace" }}>{val}</div>
                    </div>
                  ))}
                </div>
              )}
              {!d.videos?.length
                ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>No videos uploaded yet.</div>
                : <div>
                    {d.videos.map((v, i) => (
                      <div key={v.video_id || i} style={{ background: '#f9f9f7', borderRadius: 10, padding: '12px 14px', marginBottom: 8, border: '1px solid #e9e9e4' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, gap: 10 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.title}</span>
                          <span style={{ fontSize: 9, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '2px 7px', borderRadius: 4, background: v.status === 'ready' ? '#f0fdf4' : v.status === 'failed' || v.status === 'rejected' ? '#fef2f2' : '#fffbeb', color: v.status === 'ready' ? '#15803d' : v.status === 'failed' || v.status === 'rejected' ? '#b91c1c' : '#92400e', flexShrink: 0, textTransform: 'uppercase' }}>{v.status}</span>
                        </div>
                        <div style={{ display: 'flex', gap: 14, fontSize: 11, color: '#6b7280', fontFamily: "'JetBrains Mono',monospace" }}>
                          <span>👁 {v.views_count ?? 0}</span>
                          <span>{fmtTime(v.created_at)}</span>
                          <span style={{ color: Number(v.price_amount) === 0 ? '#15803d' : '#b45309', fontWeight: 700 }}>{Number(v.price_amount) === 0 ? 'Free' : 'Premium'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
              }
            </>
          )}

          {/* ── Live History ── */}
          {!detailLoad && tab === 'live' && (
            !d.live_history?.length
              ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>No past live streams recorded.</div>
              : <div>
                  {d.live_history.map((h, i) => (
                    <div key={h.live_id || i} style={{ background: '#f9f9f7', borderRadius: 10, padding: '12px 14px', marginBottom: 8, border: '1px solid #e9e9e4' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 700 }}>{fmtTime(h.started_at)}</span>
                        <span style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{h.duration_secs ? `${Math.round(h.duration_secs / 60)}m` : '—'}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 14, fontSize: 11, color: '#6b7280', fontFamily: "'JetBrains Mono',monospace" }}>
                        <span>👁 {h.peak_viewers ?? 0}</span>
                        <span>💬 {h.total_messages ?? 0}</span>
                        <span>❤️ {h.total_likes ?? 0}</span>
                        {h.total_revenue != null && <span style={{ color: '#15803d', fontWeight: 700 }}>{money(h.total_revenue)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
          )}
        </div>

        {/* Footer actions */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #e9e9e4', display: 'flex', gap: 10 }}>
          {d.status === 1 ? (
            <button onClick={() => onStatusChange(d, 0)} style={{ flex: 1, height: 40, borderRadius: 8, border: 'none', cursor: 'pointer', background: '#fef2f2', color: '#b91c1c', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Suspend</button>
          ) : (
            <button onClick={() => onStatusChange(d, 1)} style={{ flex: 1, height: 40, borderRadius: 8, border: 'none', cursor: 'pointer', background: '#f0fdf4', color: '#15803d', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Reinstate</button>
          )}
          <button onClick={() => setEditing(true)} style={{ flex: 1, height: 40, borderRadius: 8, border: '1px solid #e9e9e4', cursor: 'pointer', background: '#fff', color: '#111317', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            ✏️ Edit Creator
          </button>
        </div>
      </div>

      {editing && (
        <EditCreatorModal
          creator={d}
          categories={categories}
          onClose={() => setEditing(false)}
          onSaved={(updated) => { setDetail(prev => ({ ...prev, ...updated })); setEditing(false) }}
        />
      )}
    </>
  )
}

// ── Suspend/Reinstate Confirm Modal ──────────────────────────────────────────
function StatusModal({ action, onClose, onConfirm, loading }) {
  if (!action) return null
  const { creator, status } = action
  const suspending = status === 0
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 300 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 420, background: '#fff', borderRadius: 14, zIndex: 301, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ padding: '28px 28px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>{suspending ? '⚠️' : '✅'}</div>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>{suspending ? 'Suspend Creator?' : 'Reinstate Creator?'}</div>
          <div style={{ fontSize: 13, color: '#9a9a94', lineHeight: 1.7 }}>
            {suspending
              ? <>This blocks <b>{creator.display_name}</b> from going live again and hides them from discovery until reinstated.</>
              : <><b>{creator.display_name}</b> will be able to go live and accept tips again.</>}
          </div>
        </div>
        <div style={{ padding: '0 28px 24px', display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading} style={{ flex: 1, height: 42, borderRadius: 8, border: 'none', background: loading ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            {loading ? 'Saving…' : suspending ? 'Yes, Suspend' : 'Yes, Reinstate'}
          </button>
        </div>
      </div>
    </>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Creators() {
  const [creators,   setCreators]   = useState([])
  const [loading,    setLoading]    = useState(true)
  const [search,     setSearch]     = useState('')
  const [status,     setStatus]     = useState('')
  const [page,        setPage]      = useState(1)
  const [totalPages,  setTotalPages] = useState(1)
  const [totalCount,  setTotalCount] = useState(0)
  const [stats,       setStats]     = useState({ total: 0, active: 0, suspended: 0, total_tips_paid: 0 })
  const [selected,    setSelected]  = useState(null)
  const [statusAction, setStatusAction] = useState(null)
  const [statusLoad,  setStatusLoad] = useState(false)
  const [categories,  setCategories] = useState([])

  const PER_PAGE = 15

  useEffect(() => {
    api.get('/creators/admin/categories')
      .then(res => setCategories(res.data.categories || []))
      .catch(() => setCategories([]))
  }, [])

  const fetchCreators = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ page, limit: PER_PAGE, ...(search && { search }), ...(status && { status }) })
      const res = await api.get(`/creators/admin/list?${params}`)
      setCreators(res.data.creators || [])
      setTotalPages(res.data.totalPages || 1)
      setTotalCount(res.data.total || 0)
      setStats(res.data.stats || { total: 0, active: 0, suspended: 0, total_tips_paid: 0 })
    } catch {
      setCreators([])
    } finally { setLoading(false) }
  }, [page, search, status])

  useEffect(() => { fetchCreators() }, [fetchCreators])
  useEffect(() => { setPage(1) }, [search, status])

  const applyStatus = async () => {
    if (!statusAction) return
    setStatusLoad(true)
    try {
      const res = await api.patch(`/creators/admin/${statusAction.creator.creator_id}/status`, { status: statusAction.status })
      setCreators(prev => prev.map(c => c.creator_id === statusAction.creator.creator_id ? { ...c, status: res.data.creator.status } : c))
      setSelected(prev => prev && prev.creator_id === statusAction.creator.creator_id ? { ...prev, status: res.data.creator.status } : prev)
      setStatusAction(null)
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update status')
    } finally { setStatusLoad(false) }
  }

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>Creators</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Manage buyers who've gone live without a shop.</div>
        </div>
        <button onClick={fetchCreators} style={{ height: 38, padding: '0 16px', borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 6 }}>
          ↻ Refresh
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24 }}>
        <StatCard label="Total Creators" value={stats.total} />
        <StatCard label="Active"          value={stats.active}    accent="#15803d" />
        <StatCard label="Suspended"       value={stats.suspended} accent="#b91c1c" />
        <StatCard label="Total Tips Paid" value={money(stats.total_tips_paid)} accent="#e8440a" />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <select value={status} onChange={e => setStatus(e.target.value)} style={selectStyle}>
          {FILTER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 8, padding: '8px 13px' }}>
          <span style={{ fontSize: 14, color: '#9a9a94' }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, category, owner phone…" style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#111317', flex: 1, fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
          {search && <span onClick={() => setSearch('')} style={{ cursor: 'pointer', color: '#9a9a94', fontSize: 16 }}>×</span>}
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', padding: '10px 20px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
          {['Creator','Category','Followers','Total Tips','Status','Joined'].map(h => (
            <div key={h} style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading creators…</div>
        ) : creators.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>No creators found.</div>
        ) : (
          creators.map((c, i) => (
            <div
              key={c.creator_id}
              onClick={() => setSelected(c)}
              style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', padding: '13px 20px', borderBottom: i < creators.length - 1 ? '1px solid #f3f3f0' : 'none', cursor: 'pointer', alignItems: 'center' }}
              onMouseEnter={e => e.currentTarget.style.background = '#fafaf8'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <CreatorAvatar name={c.display_name} avatar={c.avatar} size={32} radius={8} fontSize={13} />
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{c.display_name || '—'}</span>
                    {c.is_live && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', display: 'inline-block', flexShrink: 0 }} />}
                  </div>
                  <div style={{ fontSize: 11, color: '#9a9a94' }}>{c.owner_name || c.owner_phone || '—'}</div>
                </div>
              </div>
              <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{c.category || '—'}</div>
              <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{c.follower_count ?? 0}</div>
              <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, color: '#15803d' }}>{money(c.total_tips)}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <StatusBadge status={c.status} />
                <VerificationBadge status={c.verification_status} />
              </div>
              <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmt(c.created_at)}</div>
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
          <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>Page {page} of {totalPages} · {totalCount.toLocaleString()} creators</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <PagBtn label="← Prev" disabled={page === 1}          onClick={() => setPage(p => p - 1)} />
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i
              return <PagBtn key={p} label={p} active={p === page} onClick={() => setPage(p)} />
            })}
            <PagBtn label="Next →" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
          </div>
        </div>
      )}

      {/* Drawer + Modal */}
      <CreatorDrawer
        creator={selected}
        categories={categories}
        onClose={() => setSelected(null)}
        onStatusChange={(creator, status) => setStatusAction({ creator, status })}
      />
      <StatusModal action={statusAction} onClose={() => setStatusAction(null)} onConfirm={applyStatus} loading={statusLoad} />
    </div>
  )
}

function PagBtn({ label, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ minWidth: 34, height: 34, borderRadius: 7, border: '1px solid #e9e9e4', background: active ? '#111317' : '#fff', color: active ? '#fff' : disabled ? '#9a9a94' : '#111317', fontSize: 12, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', padding: '0 10px', fontFamily: "'Plus Jakarta Sans',sans-serif", opacity: disabled ? 0.4 : 1 }}>
      {label}
    </button>
  )
}
